import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';

import { theme, useResponsive } from '@sg/garnish';

import { Address, useAddressHandlers as useAddressHandlersV2 } from '@order/features-locations';

import { useCustomer } from '../../context';
import { HorizontalSafeAreaContainer } from '../HorizontalSafeAreaContainer';
import { Modal } from '../Modal';

export const AddressAddModal = (props: AddressAddModalProps) => {
  const { visible, onRequestClose } = props;

  // ─────────────────────────────────────────────────────────────────────────────

  const { customer } = useCustomer();
  const customerAddresses = customer?.addresses ?? [];

  const { formatMessage } = useIntl();
  const { currentBreakpoint, match } = useResponsive();

  const { handleOnSave: handleOnSaveV2, isAddingAddress: isAddingAddressV2 } = useAddressHandlersV2(
    { onSuccess: onRequestClose },
  );

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <Modal
      fitHeight
      visible={visible}
      accessibilityLabel={formatMessage(messages.modalHeadline)}
      presentationStyle={currentBreakpoint.isXS ? 'pageSheet' : undefined}
      size={match(['full', 'small'])}
      containerStyle={match([undefined, styles.containerSM])}
      withSafeAreaView={false}
      onRequestClose={onRequestClose}
    >
      <HorizontalSafeAreaContainer>
        <Modal.Header onClose={onRequestClose}>
          <FormattedMessage {...messages.modalHeadline} />
        </Modal.Header>

        <Address.Form
          visible={visible}
          customerAddresses={customerAddresses}
          onSubmit={handleOnSaveV2}
          isLoading={isAddingAddressV2}
        />
      </HorizontalSafeAreaContainer>
    </Modal>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerSM: {
    paddingTop: theme.spacing['6'],
    paddingBottom: theme.spacing['6'],
  },
});

// ─── Messages ───────────────────────────────────────────────────────────────────

const messages = defineMessages({
  saveAddress: {
    defaultMessage: 'Save',
    description: 'Account | Add Address Form | Save',
  },
  modalHeadline: {
    defaultMessage: 'Add Address',
    description: 'Account | Modal header | Add Address',
  },
});

// ─── Types ─────────────────────────────────────────────────────────────

type AddressAddModalProps = Readonly<{
  visible: boolean;
  onRequestClose: () => void;
}>;
